import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Select, Typography } from 'antd';
import { CircleX, Info } from 'lucide-react';
import { useRef, useState } from 'react';
import kpLogo from '@/assets/kp-logo.png';
import {
  APP_LINK_ACCEPTED_MESSAGE,
  APP_LINK_DEFAULT_PLATFORM,
  APP_LINK_PLATFORMS,
  requestAppLinkSchema,
  type RequestAppLinkInput,
} from '@pams/shared';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { useZodForm } from '@/lib/zodForm';
import { DOWNLOAD_APP_COPY as COPY, PLATFORM_LABEL } from './copy';
import { appLinkDeviceId } from './deviceId';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [APP-LINK] "SEND ME THE APP" — PUBLIC, AND IT TELLS EVERY NUMBER THE SAME THING.
 *
 *  Anybody may open `/download-app`, type a mobile number and pick a phone type. The API answers
 *  `202` for every well-formed number, registered or not, and texts the store link only to an
 *  officer who may sign in. So this page shows ONE sentence after any 2xx —
 *  `APP_LINK_ACCEPTED_MESSAGE` from `@pams/shared` — and never anything from the response: the
 *  client drops the envelope's `message` anyway, and a sentence that varied by number would be the
 *  enumeration oracle the endpoint exists not to be.
 *
 *  ⚠️ INFO, NOT SUCCESS. A green "sent" would be a claim this page cannot make — it does not know
 *  whether the number is an officer's, and must not.
 *
 *  ⚠️ IT BRINGS ITS OWN SCROLLER. Outside `AppShell` the window never scrolls (`html, body` are
 *  `overflow: hidden`), and AntD's `.ant-app` div breaks an `h-full` chain from `#root` — so
 *  Login's `min-h-screen` alone would clip this form on a short or landscape phone, with the button
 *  out of reach. A fixed box inset to the viewport is `pwa`'s answer, scoped to the page and never
 *  by relaxing `index.css` (CLAUDE.md §8 [C1c §6]).
 *
 *  ⚠️ NO `useAuth()`. The page behaves the same signed in or out, and it reads nothing.
 *
 *  ⚠️ NO TOOLTIP, MODAL, DROPDOWN, DRAWER, SHEET OR DIALOG — `e2e/specs/census.spec.ts` would need
 *  an entry for each. A `Select` and an `Alert` are not overlays it counts.
 *
 *  ⚠️ THE BRAND MARK IS A SIBLING OF THE HEADING, never inside it — Login's rule, for Login's
 *  reason: an image and a wordmark inside the `<h3>` would become part of its accessible name.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** Built from the shared tuple, so the options are exactly the values the API's enum accepts. */
const PLATFORM_OPTIONS = APP_LINK_PLATFORMS.map((value) => ({ value, label: PLATFORM_LABEL[value] }));

/**
 * ⚠️ LOGIN'S RULE ([P4-UX.1]): THE USEFUL SENTENCE IS IN `details` WHEN THERE IS ONE. A 422's
 * `message` is the generic "Request validation failed", and the issue under it is the sentence
 * written for the person. A 429's sentence is the server's, rendered as it came: it says how long
 * to wait, and nothing about whether the number is registered. A network failure is the client's
 * own `ApiError(0)`, "Could not reach the server".
 */
const describeRefusal = (cause: ApiError): string => {
  const issues: unknown[] = Array.isArray(cause.details) ? cause.details : [];
  const first = issues.find(
    (issue): issue is { message: string } =>
      typeof issue === 'object' && issue !== null && typeof (issue as { message?: unknown }).message === 'string',
  );
  return first?.message ?? cause.message;
};

const DownloadAppPage = () => {
  const { form, field, submit } = useZodForm(requestAppLinkSchema);
  const [error, setError] = useState<string | undefined>();
  const [accepted, setAccepted] = useState(false);

  /**
   * ⚠️ ONE REQUEST AT A TIME. Every request spends one of the number's few daily requests and one of
   * the device's hourly ones, and sends an SMS if the number is an officer's. AntD's `loading` refuses
   * a click only after React has re-rendered following `mutate` and an effect has run; a ref is current
   * the instant it is set, so this guard does not depend on render timing at all.
   */
  const sending = useRef(false);

  const request = useMutation({
    mutationFn: (body: RequestAppLinkInput) => api.post<null>('/auth/request-app-link', body),
    onSuccess: () => {
      setError(undefined);
      setAccepted(true);
    },
    onError: (cause: ApiError) => {
      setError(describeRefusal(cause));
    },
    onSettled: () => {
      sending.current = false;
    },
  });

  /**
   * ⚠️ `resetFields()` IS WHAT EMPTIES THE FORM, NOT THE REMOUNT. `Form.useForm()` is called here, in
   * the page, and rc-field-form's `preserve` defaults to `true` — so the store outlives the `<Form>`
   * the confirmation replaced, and remounting it would bring back the number just sent (Login's
   * [LIFECYCLE] lesson). The reset goes back to `initialValues`: an empty number, Android.
   */
  const startOver = () => {
    form.resetFields();
    setError(undefined);
    setAccepted(false);
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-background" data-testid="download-app">
      <div className="flex min-h-full items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {/* The image is decorative: the wordmark beside it carries the name, and an `alt` would say it twice. */}
          <div className="mb-6 flex items-center gap-2 text-muted-foreground">
            <img src={kpLogo} alt="" aria-hidden className="size-5" width={20} height={20} />
            <span className="text-sm font-semibold tracking-tight">{COPY.wordmark}</span>
          </div>

          <Typography.Title level={3} className="mb-1">
            {COPY.title}
          </Typography.Title>
          <p className="mb-6 text-sm text-muted-foreground">{COPY.subtitle}</p>

          {/*
           * ⚠️ THE ALERT ICONS ARE LUCIDE's, `aria-hidden`. AntD's own carry `role="img"` and an
           * `aria-label` ("close-circle", "info-circle") INSIDE the region, so a screen reader would read
           * the icon's name before the sentence. The colour still comes from the Alert's type.
           */}
          {error && (
            <Alert
              type="error"
              showIcon
              icon={<CircleX aria-hidden className="size-4" />}
              className="mb-4"
              message={error}
              data-testid="download-app-error"
            />
          )}

          {/*
           * ⚠️ THE LIVE REGION IS ALWAYS MOUNTED, AND THE ALERT IS ONLY ITS LOOK. A region inserted
           * together with its text is not reliably announced, so this one is in the DOM from the first
           * paint and the confirmation is inserted INTO it. `role="none"` on the Alert is deliberate:
           * AntD gives every Alert `role="alert"`, which is assertive, and an alert inside this region
           * would interrupt and be read twice for a sentence that is information, not an error.
           */}
          <div role="status" data-testid="download-app-accepted">
            {accepted && (
              <Alert
                type="info"
                showIcon
                icon={<Info aria-hidden className="size-4" />}
                role="none"
                className="mb-4"
                message={APP_LINK_ACCEPTED_MESSAGE}
              />
            )}
          </div>

          {accepted ? (
            /*
             * ⚠️ `autoFocus`, because the button that had focus is gone with the form: without a new
             * home, focus falls to `<body>` and a keyboard user starts again from the top of the page.
             */
            <Button block size="large" autoFocus onClick={startOver}>
              {COPY.again}
            </Button>
          ) : (
            <Form
              form={form}
              layout="vertical"
              /*
               * ⚠️ `phone: ''`, NOT ABSENT. An untouched field would hand zod `undefined` and show its
               * generic "expected string" text; an empty string reaches `phoneSchema` and gets the
               * shared sentence, "Phone number is required.".
               */
              initialValues={{ phone: '', platform: APP_LINK_DEFAULT_PLATFORM }}
              onFinish={(values) => {
                if (sending.current) return;
                // The shared schema's OUTPUT is what goes: `+91 98765 43210` leaves as `9876543210`.
                const parsed = submit(values);
                if (!parsed.ok) return;
                const deviceId = appLinkDeviceId();
                sending.current = true;
                setError(undefined);
                request.mutate({
                  phone: parsed.data.phone,
                  platform: parsed.data.platform,
                  ...(deviceId ? { deviceId } : {}),
                });
              }}
            >
              {/*
               * ⚠️ A PLAIN `Input`, NOT `components/PhoneInput.tsx`. That component drops the `id` and
               * `aria-*` props `Form.Item` injects, so this label would point at nothing, and it cuts an
               * autofilled `+91 98765 43210` down to `9198765432` — ten valid digits, the wrong number.
               * Here the whole value reaches `phoneSchema`, which normalises it on submit, and there is
               * no `maxLength` to truncate it first.
               *
               * ⚠️ [FIX-2] `validateTrigger={[]}`: the rule fires on submit only, never at a keystroke.
               *
               * ⚠️ `size="large"` ON ALL THREE CONTROLS: AntD's large size is `fontSizeLG`, 16px. Safari on
               * an iPhone ZOOMS the page when it focuses a field under 16px, and this page is opened on
               * phones — the iOS option says so.
               */}
              <Form.Item {...field('phone')} label={COPY.phoneLabel} validateTrigger={[]}>
                <Input
                  size="large"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  autoFocus
                  placeholder={COPY.phonePlaceholder}
                  data-testid="download-app-phone"
                />
              </Form.Item>

              {/* No `allowClear`: a cleared phone type is not a choice the API has a link for. */}
              <Form.Item {...field('platform')} label={COPY.platformLabel}>
                <Select size="large" options={PLATFORM_OPTIONS} data-testid="download-app-platform" />
              </Form.Item>

              <Button type="primary" htmlType="submit" block size="large" loading={request.isPending}>
                {COPY.submit}
              </Button>
            </Form>
          )}

          {/*
           * The vendor credit, exactly as on the sign-in page. ⚠️ `rel="noopener noreferrer"` is not
           * optional on `target="_blank"`: without `noopener` the opened page holds a `window.opener`
           * handle back into this one and can navigate it.
           */}
          <p className="mt-10 text-center text-xs text-muted-foreground">
            Powered by{' '}
            <a
              href="https://mindwebs.org"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-link underline-offset-2 hover:underline"
            >
              Mind Webs Ventures
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadAppPage;
